import React from "react";
import { useAuth } from "../../context/AuthContext";
import {
  ShieldCheck,
  User,
  Mail,
  Phone,
  Building,
  GraduationCap,
  MapPin,
  Lock,
} from "lucide-react";

export const StudentProfile = () => {
  const { userProfile, currentUser } = useAuth();

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Verified Student Profile</h1>
          <p className="text-xs text-muted-foreground font-mono">
            Kian Technologies • 15 Days Free Cyber Security Masterclass
          </p>
        </div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs font-mono self-start sm:self-auto">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Verified Student Credential</span>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm space-y-8">
        {/* Top ID Card Header */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 pb-6 border-b border-border">
          {userProfile?.photoUrl ? (
            <img
              src={userProfile.photoUrl}
              alt="Passport Photo"
              data-testid="student-profile-photo"
              className="w-28 h-32 rounded-xl object-cover border-2 border-emerald-500/40 shadow-md flex-shrink-0"
            />
          ) : (
            <div className="w-28 h-32 rounded-xl bg-muted border border-border flex items-center justify-center text-muted-foreground flex-shrink-0">
              <User className="w-12 h-12 opacity-40" />
            </div>
          )}

          <div className="space-y-2 text-center sm:text-left flex-1">
            <div className="text-xs font-mono text-muted-foreground uppercase">
              Official Masterclass ID
            </div>
            <div
              data-testid="student-id-display"
              className="text-2xl sm:text-3xl font-extrabold text-emerald-400 font-mono tracking-wider"
            >
              {userProfile?.studentId || "KIAN-CYBER-PENDING"}
            </div>
            <h2 className="text-xl font-bold text-foreground">
              {userProfile?.fullName || currentUser?.email}
            </h2>
            <p className="text-xs font-mono text-muted-foreground">
              Status: <span className="text-emerald-400 font-bold uppercase">{userProfile?.status || "active"}</span>
            </p>
          </div>
        </div>

        {/* Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Academic info */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-foreground flex items-center gap-2 border-b border-border pb-2">
              <GraduationCap className="w-4 h-4 text-cyan-400" />
              <span>Academic Details</span>
            </h3>

            <div className="space-y-3 text-xs font-mono">
              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <span className="text-muted-foreground block text-[11px]">College / Institute</span>
                <span className="font-semibold text-foreground text-sm">
                  {userProfile?.college || "N/A"}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <span className="text-muted-foreground block text-[11px]">Course & Degree</span>
                <span className="font-semibold text-foreground">
                  {userProfile?.course || "N/A"}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <span className="text-muted-foreground block text-[11px]">Branch / Stream</span>
                <span className="font-semibold text-foreground">
                  {userProfile?.branch || "N/A"}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <span className="text-muted-foreground block text-[11px]">Year & Semester</span>
                <span className="font-semibold text-foreground">
                  {userProfile?.yearSemester || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Contact & Location info */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-foreground flex items-center gap-2 border-b border-border pb-2">
              <User className="w-4 h-4 text-emerald-400" />
              <span>Contact & Location Details</span>
            </h3>

            <div className="space-y-3 text-xs font-mono">
              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <span className="text-muted-foreground block text-[11px]">Email Address</span>
                <span className="font-semibold text-foreground truncate block">
                  {userProfile?.email || currentUser?.email || "N/A"}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <span className="text-muted-foreground block text-[11px]">Mobile Number</span>
                <span className="font-semibold text-foreground">
                  {userProfile?.mobile || "N/A"}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <span className="text-muted-foreground block text-[11px]">City / Location</span>
                <span className="font-semibold text-foreground">
                  {userProfile?.city || "Bhilai"}
                  {userProfile?.state ? `, ${userProfile.state}` : ""}
                </span>
              </div>

              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <span className="text-muted-foreground block text-[11px]">Cyber Track Focus</span>
                <span className="font-semibold text-emerald-400">
                  {userProfile?.interestedArea || "Cyber Security"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="p-4 rounded-xl bg-muted/30 border border-border text-xs text-muted-foreground flex items-start gap-3">
          <Lock className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            Important verified information such as Student ID, Institute, and registered credentials are verified and locked to preserve identity integrity on the final Masterclass certification.
          </p>
        </div>
      </div>
    </div>
  );
};
